import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { Icon } from '../ui/Icon'
import { Button } from '../ui/Button'
import { useAppStore } from '../../store/useAppStore'
import { Theme } from '../../types'
import { useLocation, useNavigate } from 'react-router-dom'
import { shareDocument, ShareOptions } from '../../services/shareService'
import { toast } from 'sonner'
import { isTauri } from '../../services/tauriService'

export const MoreMenu: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<'none' | 'language' | 'display'>('none')
  const menuRef = useRef<HTMLDivElement>(null)
  
  const { theme, setTheme, currentDocument } = useAppStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [isSharing, setIsSharing] = useState(false)

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setActiveSubmenu('none')
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleLanguageChange = (lang: string) => {
    const segments = location.pathname.split('/').filter(Boolean)
    segments[0] = lang
    const newPath = '/' + segments.join('/')
    i18n.changeLanguage(lang)
    navigate(newPath)
    setIsOpen(false)
  }

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme)
    setIsOpen(false)
  }

  const handleShare = async () => {
    if (!currentDocument) return
    setIsSharing(true)
    try {
      const options: ShareOptions = { expiresHours: 24 }
      const shareInfo = await shareDocument(currentDocument, options)
      await navigator.clipboard.writeText(shareInfo.shareUrl)
      toast.success(t('share.success'))
      setIsOpen(false)
    } catch (error) {
      console.error('Failed to share document:', error)
      toast.error(t('share.error'))
    } finally {
      setIsSharing(false)
    }
  }

  const currentLangLabel = i18n.language === 'zh' ? t('language.zh') : t('language.en')
  const currentThemeLabel = theme === 'auto' ? t('theme.system') : (theme === 'dark' ? t('theme.dark') : t('theme.light'))

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="gap-2 whitespace-nowrap flex-shrink-0"
        title={t('nav.more')}
      >
        <Icon name="more" size={16} />
        <span className="hidden sm:inline">{t('nav.more')}</span>
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-fade-in">
          {/* Share Action */}
          <button
            onClick={handleShare}
            disabled={isSharing || !currentDocument}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <Icon name={isSharing ? 'loader' : 'share'} size={18} className="text-gray-500" />
              <span>{t('nav.share')}</span>
            </div>
          </button>

          {/* Download Mac App - Only show in web browser, not in Tauri desktop app */}
          {!isTauri() && (
            <a
              href="https://github.com/yvente/apps/releases/download/MarkFlow-v1.0.0/MarkFlow_1.0.0_aarch64.dmg"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <div className="flex items-center gap-3">
                <Icon name="apple" size={18} className="text-gray-500" />
                <span>{t('nav.downloadApp')}</span>
              </div>
              <Icon name="download" size={14} className="text-gray-400" />
            </a>
          )}

          <div className="my-1 border-t border-gray-100 dark:border-gray-700/50" />

          {/* Display Submenu Trigger */}
          <div className="relative group">
            <button
              onMouseEnter={() => setActiveSubmenu('display')}
              onClick={() => setActiveSubmenu(activeSubmenu === 'display' ? 'none' : 'display')}
              className={clsx(
                "w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors",
                activeSubmenu === 'display' ? "bg-gray-50 dark:bg-gray-700/50" : "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                "text-gray-700 dark:text-gray-200"
              )}
            >
              <div className="flex items-center gap-3">
                <Icon name="settings" size={18} className="text-gray-500" />
                <span>{t('nav.display')}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <span>{currentThemeLabel}</span>
                <Icon name="chevron-right" size={14} className={clsx("transition-transform sm:rotate-0", activeSubmenu === 'display' && "rotate-90")} />
              </div>
            </button>

            {/* Desktop: 左侧弹出子菜单 */}
            {activeSubmenu === 'display' && (
              <div 
                className="hidden sm:block absolute right-full top-0 mr-1 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 animate-fade-in"
                onMouseLeave={() => setActiveSubmenu('none')}
              >
                {[
                  { id: 'light', label: t('theme.light'), icon: 'sun' },
                  { id: 'dark', label: t('theme.dark'), icon: 'moon' },
                  { id: 'auto', label: t('theme.system'), icon: 'desktop' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => handleThemeChange(opt.id as Theme)}
                    className={clsx(
                      "w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors",
                      theme === opt.id ? "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20" : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    )}
                  >
                    <Icon name={opt.icon as any} size={16} />
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Mobile: 内联展开选项 */}
            {activeSubmenu === 'display' && (
              <div className="sm:hidden bg-gray-50 dark:bg-gray-700/30 py-1">
                {[
                  { id: 'light', label: t('theme.light'), icon: 'sun' },
                  { id: 'dark', label: t('theme.dark'), icon: 'moon' },
                  { id: 'auto', label: t('theme.system'), icon: 'desktop' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => handleThemeChange(opt.id as Theme)}
                    className={clsx(
                      "w-full flex items-center gap-3 pl-10 pr-4 py-2 text-sm transition-colors",
                      theme === opt.id ? "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20" : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600/50"
                    )}
                  >
                    <Icon name={opt.icon as any} size={16} />
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Language Submenu Trigger */}
          <div className="relative group">
            <button
              onMouseEnter={() => setActiveSubmenu('language')}
              onClick={() => setActiveSubmenu(activeSubmenu === 'language' ? 'none' : 'language')}
              className={clsx(
                "w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors",
                activeSubmenu === 'language' ? "bg-gray-50 dark:bg-gray-700/50" : "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                "text-gray-700 dark:text-gray-200"
              )}
            >
              <div className="flex items-center gap-3">
                <Icon name="globe" size={18} className="text-gray-500" />
                <span>{t('nav.language')}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <span>{currentLangLabel}</span>
                <Icon name="chevron-right" size={14} className={clsx("transition-transform sm:rotate-0", activeSubmenu === 'language' && "rotate-90")} />
              </div>
            </button>

            {/* Desktop: 左侧弹出子菜单 */}
            {activeSubmenu === 'language' && (
              <div 
                className="hidden sm:block absolute right-full top-0 mr-1 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 animate-fade-in"
                onMouseLeave={() => setActiveSubmenu('none')}
              >
                {[
                  { id: 'en', label: t('language.en') },
                  { id: 'zh', label: t('language.zh') }
                ].map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => handleLanguageChange(lang.id)}
                    className={clsx(
                      "w-full flex items-center px-4 py-2 text-sm transition-colors",
                      i18n.language.startsWith(lang.id) ? "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20" : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    )}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}

            {/* Mobile: 内联展开选项 */}
            {activeSubmenu === 'language' && (
              <div className="sm:hidden bg-gray-50 dark:bg-gray-700/30 py-1">
                {[
                  { id: 'en', label: t('language.en') },
                  { id: 'zh', label: t('language.zh') }
                ].map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => handleLanguageChange(lang.id)}
                    className={clsx(
                      "w-full flex items-center pl-10 pr-4 py-2 text-sm transition-colors",
                      i18n.language.startsWith(lang.id) ? "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20" : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600/50"
                    )}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Contact Section */}
          <div className="my-1 border-t border-gray-100 dark:border-gray-700/50" />

          {/* Contact Info */}
          <div className="px-4 py-2.5">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('nav.contact')}
            </div>
            <a
              href="mailto:yvente@163.com"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Icon name="mail" size={16} className="text-gray-500" />
              <span>yvente@163.com</span>
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
